/**
 * <cart-samples> — lets a customer indicate N samples of their choice.
 * Selections are saved as the "Sample choices" cart attribute and mirrored
 * into the order note via the AJAX Cart API. No line items, no price.
 * Registered as a custom element so it re-upgrades automatically when the
 * cart / cart-drawer re-renders its HTML.
 */
if (!customElements.get('cart-samples')) {
  const sampleNotePrefix = 'Samples:';
  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const buildOrderNote = (currentNote, values) => {
    const customerNote = String(currentNote || '')
      .split(/\r?\n/)
      .filter((line) => !line.trim().startsWith(sampleNotePrefix))
      .join('\n')
      .trim();
    const sampleNote = values.length ? `${sampleNotePrefix} ${values.join(', ')}` : '';

    return [customerNote, sampleNote].filter(Boolean).join('\n\n');
  };

  customElements.define(
    'cart-samples',
    class CartSamples extends HTMLElement {
      constructor() {
        super();
        this.max = parseInt(this.dataset.max, 10) || 2;
        this.trigger = this.querySelector('.cart-samples__trigger');
        this.dialog = this.querySelector('.cart-samples__dialog');
        this.overlay = this.querySelector('[data-samples-overlay]');
        this.closeBtn = this.querySelector('.cart-samples__close');
        this.search = this.querySelector('[data-samples-search]');
        this.saveBtn = this.querySelector('.cart-samples__save');
        this.hint = this.querySelector('[data-samples-count]');
        this.attributeInput = this.querySelector('[data-samples-attribute]');
        this.formId = this.attributeInput && this.attributeInput.getAttribute('form');
        this.checkboxes = Array.from(this.querySelectorAll('.cart-samples__checkbox'));
        this.items = Array.from(this.querySelectorAll('.cart-samples__item'));

        this.onKeydown = this.onKeydown.bind(this);

        this.trigger?.addEventListener('click', () => this.open());
        this.closeBtn?.addEventListener('click', () => this.close());
        this.overlay?.addEventListener('click', () => this.close());
        this.search?.addEventListener('input', () => this.filter());
        this.saveBtn?.addEventListener('click', () => this.save());
        this.checkboxes.forEach((cb) => cb.addEventListener('change', () => this.enforceLimit()));

        this.enforceLimit();
      }

      open() {
        this.dialog.hidden = false;
        this.trigger?.setAttribute('aria-expanded', 'true');
        this.scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        this.search?.focus();
      }

      close() {
        this.dialog.hidden = true;
        this.trigger?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKeydown);
        this.trigger?.focus();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      selected() {
        return this.checkboxes.filter((cb) => cb.checked);
      }

      enforceLimit() {
        const count = this.selected().length;
        const atMax = count >= this.max;
        this.checkboxes.forEach((cb) => {
          cb.disabled = atMax && !cb.checked;
        });
        if (this.hint) {
          this.hint.textContent = count ? `${count} of ${this.max} selected` : `Select up to ${this.max}.`;
        }
      }

      filter() {
        const q = this.search.value.trim().toLowerCase();
        this.items.forEach((li) => {
          const title = li.dataset.title || '';
          li.hidden = q !== '' && !title.includes(q);
        });
      }

      async save() {
        const values = this.selected().map((cb) => cb.value);
        const serializedValues = values.join(', ');
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const noteInputs = Array.from(document.querySelectorAll('[data-samples-note]'));
        const currentNoteInput = noteInputs.find((input) => input.getAttribute('form') === this.formId);
        const orderNote = buildOrderNote(currentNoteInput ? currentNoteInput.value : '', values);

        // The form-associated input guarantees that a normal checkout submit
        // carries the choice even if the AJAX request is interrupted.
        if (this.attributeInput) this.attributeInput.value = serializedValues;
        noteInputs.forEach((input) => {
          input.value = orderNote;
        });
        this.saveBtn.classList.add('loading');
        this.saveBtn.disabled = true;
        try {
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              attributes: { 'Sample choices': serializedValues },
              note: orderNote,
            }),
          });

          if (!response.ok) {
            throw new Error(`Cart update failed with status ${response.status}`);
          }

          const updatedCart = await response.json();
          const savedValues = updatedCart.attributes && updatedCart.attributes['Sample choices'];
          if ((savedValues || '') !== serializedValues) {
            throw new Error('Shopify did not return the saved sample choices');
          }
          if ((updatedCart.note || '') !== orderNote) {
            throw new Error('Shopify did not return the saved sample order note');
          }

          // A cart page can contain both the page and drawer pickers. Keep
          // their checkout-form fields and visible summaries in sync.
          document.querySelectorAll('cart-samples').forEach((picker) => {
            if (typeof picker.applySavedSelection === 'function') picker.applySavedSelection(values);
          });
          this.close();
        } catch (error) {
          console.error('[cart-samples] Failed to save selection', error);
          if (this.hint) this.hint.textContent = 'Your samples could not be saved. Please try again.';
        } finally {
          this.saveBtn.classList.remove('loading');
          this.saveBtn.disabled = false;
        }
      }

      applySavedSelection(values) {
        const serializedValues = values.join(', ');
        if (this.attributeInput) this.attributeInput.value = serializedValues;
        this.checkboxes.forEach((checkbox) => {
          checkbox.checked = values.includes(checkbox.value);
        });
        this.enforceLimit();
        this.updateTrigger(values);
      }

      updateTrigger(values) {
        if (!this.trigger) return;
        if (values.length) {
          this.trigger.innerHTML =
            `<span class="cart-samples__trigger-label">Your samples: <strong>${values.map(escapeHtml).join(', ')}</strong></span>` +
            `<span class="cart-samples__trigger-edit">Edit</span>`;
        } else {
          this.trigger.textContent = 'Click here to indicate 2 samples of your choice';
        }
      }
    }
  );
}
